<script>

import useSharedData from "@/use/SharedData";
import PaySlipFilter from "@/views/PaySlip/Components/PaySlipFilter.vue";
import {usePaySlipAnnexStore} from "@/stores/PaySlipAnnex";
import ImpressionModels from "@/views/Impression/Components/ImpressionModels.vue";
import {ref, watch} from "vue";
import ContributionDeclarationPage from "@/views/ContributionDeclaration/ContributionDeclarationPage.vue";
import axios from "axios";
import useSharedMethod from "@/use/SharedMethod";

export default {
    props: {
        type: String,
    },
    components: {ContributionDeclarations: ContributionDeclarationPage, ImpressionModels, PaySlipFilter},
    setup(props) {
        const annexStore = usePaySlipAnnexStore();
        const {filter} = useSharedData();
        const {transformFilterValues} = useSharedMethod();


        filter.value = {
            phase: null,
            closed: null,
        };


        function clearFilter() {
            Object.keys(filter.value).forEach(key => {
                if (key === 'annexId') {
                    filter.value[key] = annexStore.getMax();
                } else {
                    filter.value[key] = null;
                }
            });
        }

        const precision = ref(2);
        const hideIfZero = ref(false);
        const title = ref(null);
        const cnasAgencyId = ref(null);
        const classe = ref(null);
        const fournirPlusTardLe = ref(null);
        const montantDeduire = ref(null);
        const chequeBancaireNumber = ref(null);
        const chequePostalNumber = ref(null);
        const certifieExacteA = ref(null);
        const certifieExacteLe = ref(null);


        const loading = ref(false);
        const documentUrl = ref(null);

        function print() {
            loading.value = true;
            documentUrl.value = null;
            axios.get(`/printed/getDeclarationCotisation`, {
                responseType: 'blob',
                params: transformFilterValues({
                    ...filter.value,
                    type: props.type,
                    montantDeduire: montantDeduire.value,
                    precision: precision.value,
                    hideIfZero: hideIfZero.value,
                    cnasAgencyId: cnasAgencyId.value,
                    classe: classe.value,
                    title: title.value,
                    fournirPlusTardLe: fournirPlusTardLe.value,
                    chequeBancaireNumber: chequeBancaireNumber.value,
                    chequePostalNumber: chequePostalNumber.value,
                    certifieExacteA: certifieExacteA.value,
                    certifieExacteLe: certifieExacteLe.value,
                })
            }).then(response => {
                const blob = response.data;
                documentUrl.value = window.URL.createObjectURL(blob);
            }).finally(() => {
                loading.value = false;
            })
        }

        watch(() => filter.value, () => {
            print();
        }, {deep: true});

        return {
            filter,
            precision,
            hideIfZero,
            title,
            cnasAgencyId,
            classe,
            fournirPlusTardLe,
            montantDeduire,
            chequeBancaireNumber,
            chequePostalNumber,
            certifieExacteA,
            certifieExacteLe,
            loading,
            documentUrl,
            clearFilter,
            print
        }
    }
}
</script>

<template>
    <div>
        <w-app
            :title="$t(`module.${type}`)"
            show-filter
            :filter="filter"
            @clearFilter="clearFilter"
        >
            <template v-slot:filter>
                <pay-slip-filter v-model="filter" auto-select-annex/>
            </template>

            <template v-slot:leftToolbar></template>
            <template v-slot:rightToolbar>
                <impression-models/>
                 <w-btn
                    icon="mdi-monitor-arrow-down"
                    color="teal"
                    :text="$t('common.download')"
                    :loading="loading"
                    @press="print"
                />
            </template>
            <template v-slot:content>
                <v-card>
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12" md="5">
                                <w-fieldset title="Info Agence">
                                    <v-row dense>
                                        <v-col cols="12">
                                            <w-text-field
                                                v-model="title"
                                                :label="$t('impression.subtitle')"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <str-cnas-agcency-autocomplete
                                                v-model="cnasAgencyId"
                                                :label="$t('module.cnas_agency')"
                                                set-default
                                                not-clearable
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <w-text-field
                                                v-model="classe"
                                                :label="$t('impression.class')"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <w-date-picker
                                                v-model="fournirPlusTardLe"
                                                :label="$t('impression.to_be_provided_no_later_than')"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <w-text-field
                                                v-model="montantDeduire"
                                                type="number"
                                                :label="$t('impression.amount_to_be_deducted')"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <w-text-field
                                                v-model="chequeBancaireNumber"
                                                :label="$t('impression.bank_check_number')"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <w-text-field
                                                v-model="chequePostalNumber"
                                                :label="$t('impression.postal_check_number')"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row dense>
                                        <v-col cols="6">
                                            <w-text-field
                                                v-model="certifieExacteA"
                                                :label="$t('impression.certified_accurate_to')"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <w-date-picker
                                                v-model="certifieExacteLe"
                                                :label="$t('impression.certified_accurate')"
                                            />
                                        </v-col>
                                        <v-col cols="4">
                                            <v-checkbox
                                                v-model="hideIfZero"
                                                :true-value="false"
                                                :false-value="true"
                                                class="mt-n1"
                                                :label="$t('impression.display_even_if_equal_to_0')"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col cols="4">
                                            <v-checkbox
                                                v-model="precision"
                                                disabled
                                                :true-value="3"
                                                :false-value="2"
                                                class="mt-n1"
                                                :label="$t('impression.display_on_3_decimal_places')"
                                                hide-details
                                            />
                                        </v-col>
                                    </v-row>
                                </w-fieldset>
                                <br/>
                                <w-fieldset :title="$t('impression.preferences')">
                                    <contribution-declarations :type="type"/>
                                </w-fieldset>
                            </v-col>
                            <v-col cols="12" md="7">
                                <v-container fluid>
                                    <iframe
                                        v-if="documentUrl"
                                        :src="documentUrl"
                                        width="100%"
                                        height="1200px"
                                        name="myIframe"
                                    />
                                </v-container>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>

<style scoped>

</style>